/**
 * BrandLogo Component
 * 
 * Intelligent logo component that automatically selects the appropriate logo variant
 * based on the current theme and requested variant. Provides robust error handling
 * and fallback mechanisms.
 * 
 * Features:
 * - Automatic theme-aware logo selection
 * - Multiple variant support (primary, dark, light, icon)
 * - Graceful fallback on load errors
 * - Accessible with proper alt text
 * - Performance optimized with useMemo
 * 
 * @module components/BrandLogo
 * @version 1.0.0
 */

import React, { useMemo, useState, useEffect, type JSX } from 'react';
import { useBrandingContext } from '../contexts/BrandingContext';
import { useTheme } from '../contexts/ThemeContext';
import { isDedicatedBrandTheme, usesLightTextHeader } from '../core/theme/brandOwnership';

/**
 * BrandLogo component props
 */
export interface BrandLogoProps {
  /**
   * Logo variant to display
   * - 'primary': Use primary logo (default), auto-selects based on theme
   * - 'dark': Explicitly use dark theme logo
   * - 'light': Explicitly use light theme logo
   * - 'icon': Use small icon variant
   * 
   * @default 'primary'
   */
  variant?: 'primary' | 'dark' | 'light' | 'icon';
  
  /**
   * Logo height in pixels
   * Width is automatically calculated to maintain aspect ratio
   * 
   * @default 32
   */
  height?: number;
  
  /**
   * Fallback logo URL if branding not loaded or logo fails to load
   * Should be a reliable local asset
   * 
   * @default '/api/configs/branding/logos/empowerid-logo-light.svg'
   */
  fallback?: string;

  /**
   * Additional CSS class name(s) to apply to the image element
   */
  className?: string;
  
  /**
   * Custom alt text for the logo
   * If not provided, uses app name from branding config
   */
  alt?: string;
  
  /**
   * Optional inline styles to apply to the image element
   */
  style?: React.CSSProperties;
  
  /**
   * Optional onClick handler
   * Useful for making logo interactive (e.g., navigate to home)
   */
  onClick?: () => void;
}

/**
 * BrandLogo Component
 * 
 * Displays the appropriate brand logo with intelligent selection logic.
 * 
 * @example Basic usage
 * ```tsx
 * <BrandLogo height={32} />
 * ```
 * 
 * @example Specific variant
 * ```tsx
 * <BrandLogo variant="dark" height={40} />
 * ```
 * 
 * @example With click handler
 * ```tsx
 * <BrandLogo onClick={() => navigate('/')} />
 * ```
 * 
 * @example Custom styling
 * ```tsx
 * <BrandLogo
 *   height={48}
 *   className="logo-custom"
 *   style={{ cursor: 'pointer' }}
 * />
 * ```
 */
export function BrandLogo({
  variant = 'primary',
  height = 32,
  fallback = '/api/configs/branding/logos/empowerid-logo-light.svg',
  className = '',
  alt,
  style,
  onClick,
}: BrandLogoProps): JSX.Element {
  const { config, loading } = useBrandingContext();
  const { theme } = useTheme();
  
  // 🎁 NEW: Check for theme-specific branding overrides!
  const [themeBranding, setThemeBranding] = useState<any>(null);
  
  useEffect(() => {
    // Listen for theme branding changes
    const handleThemeBranding = (e: any) => {
      // e.detail is null when theme branding should be cleared
      setThemeBranding(e.detail || null);
    };
    
    window.addEventListener('theme-branding-changed', handleThemeBranding);
    
    // Also check if theme branding already set
    if ((window as any).__THEME_BRANDING__) {
      setThemeBranding((window as any).__THEME_BRANDING__);
    }
    
    return () => window.removeEventListener('theme-branding-changed', handleThemeBranding);
  }, []);

  /**
   * Select appropriate logo URL based on variant and theme
   * Memoized for performance - only recomputes when dependencies change
   */
  const logoUrl = useMemo(() => {
    // 🎁 Priority 1: Theme branding (if theme includes logo)
    if (themeBranding?.logos) {
      const themeLogo = themeBranding.logos;
      
      if (variant === 'icon' && themeLogo.icon) return themeLogo.icon;
      if (variant === 'dark' && themeLogo.dark) return themeLogo.dark;
      if (variant === 'light' && themeLogo.light) return themeLogo.light;
      
      // Auto-select based on theme
      if (variant === 'primary') {
        if (theme === 'black' && themeLogo.dark) return themeLogo.dark;
        if (usesLightTextHeader(theme) && themeLogo.dark) return themeLogo.dark;
        // Light themes use dark-text wordmark (logos.light)
        if ((theme === 'white' || theme === 'nomadid' || theme === 'empowerid') && themeLogo.light) return themeLogo.light;
      }
      
      if (themeLogo.primary) return themeLogo.primary;
    }

    // Dedicated tenant/product themes must never blend in global EmpowerID logos
    if (isDedicatedBrandTheme(theme)) {
      return '';
    }
    
    // Priority 2: Branding config (default)
    // Use fallback while loading or if no logos configured
    if (!config?.logos) {
      return fallback;
    }

    const logos = config.logos;

    // Explicit icon variant requested
    if (variant === 'icon') {
      return logos.icon || logos.primary || fallback;
    }

    // Explicit dark variant requested
    if (variant === 'dark') {
      return logos.dark || logos.primary || fallback;
    }

    // Explicit light variant requested
    if (variant === 'light') {
      return logos.light || logos.primary || fallback;
    }

    // Primary variant - auto-select based on current theme
    if (variant === 'primary') {
      // Black theme - prefer dark logo
      if (theme === 'black' && logos.dark) {
        return logos.dark;
      }

      if (usesLightTextHeader(theme) && logos.dark) {
        return logos.dark;
      }
      
      // Light themes - prefer dark-text wordmark (logos.light)
      if ((theme === 'white' || theme === 'nomadid' || theme === 'empowerid') && logos.light) {
        return logos.light;
      }
      
      // Blue theme or fallback - use primary
      return logos.primary || fallback;
    }

    // Shouldn't reach here, but fallback just in case
    return logos.primary || fallback;
  }, [config, theme, variant, fallback, themeBranding]);  // Added themeBranding dependency!

  /**
   * Generate accessible alt text
   * Uses custom alt if provided, otherwise app name from config
   */
  const altText = useMemo(() => {
    if (alt) return alt;
    if (config?.appName) return config.appName;
    return 'Logo';
  }, [alt, config]);

  /**
   * Combined inline styles
   * Merges default styles with custom styles
   */
  const combinedStyle: React.CSSProperties = useMemo(() => ({
    height,
    width: 'auto',  // Maintain aspect ratio
    display: 'block',
    ...style,  // Allow custom styles to override defaults
  }), [height, style]);

  /**
   * Handle image load error
   * Falls back to fallback URL if primary logo fails to load
   */
  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.currentTarget;

    if (themeBranding?.logos) {
      const themeFallback =
        themeBranding.logos.primary ||
        themeBranding.logos.dark ||
        themeBranding.logos.light;
      if (themeFallback && !target.src.endsWith(themeFallback)) {
        console.warn(`[BrandLogo] Failed to load logo from ${target.src}, using theme branding fallback`);
        target.src = themeFallback;
        return;
      }
    }

    if (isDedicatedBrandTheme(theme)) {
      console.error('[BrandLogo] Failed to load dedicated theme logo');
      return;
    }
    
    // Only attempt platform fallback if not already using it
    if (target.src !== fallback) {
      console.warn(`[BrandLogo] Failed to load logo from ${target.src}, using fallback`);
      target.src = fallback;
    } else {
      console.error('[BrandLogo] Failed to load fallback logo');
    }
  };

  /**
   * Optional loading state
   * Show placeholder while branding is loading
   */
  if (loading || (isDedicatedBrandTheme(theme) && !logoUrl)) {
    return (
      <div
        className={`brand-logo-placeholder ${className}`}
        style={{
          ...combinedStyle,
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '4px',
        }}
        role="img"
        aria-label="Loading logo..."
      />
    );
  }

  return (
    <img
      src={logoUrl}
      alt={altText}
      className={className}
      style={combinedStyle}
      onClick={onClick}
      onError={handleError}
      // Accessibility: mark as decorative if alt text explicitly empty
      {...(alt === '' ? { role: 'presentation' } : {})}
    />
  );
}

/**
 * BrandLogo loading skeleton
 * Used while branding configuration is being fetched
 */
export function BrandLogoSkeleton({
  height = 32,
  className = '',
}: Pick<BrandLogoProps, 'height' | 'className'>): JSX.Element {
  return (
    <div
      className={`brand-logo-skeleton ${className}`}
      style={{
        height,
        width: height * 4,  // Assume 4:1 aspect ratio
        background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.05) 100%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite',
        borderRadius: '4px',
      }}
      role="img"
      aria-label="Loading logo..."
    />
  );
}

// Export default for convenience
export default BrandLogo;

